<script setup lang="ts">
/**
 * Расшифровка созвона — блок 12-9Г (эпик DEV-2088, решение 6 гриля).
 *
 * Свёрнута по умолчанию: за конспектом приходят чаще, чем за дословным
 * текстом, а развёрнутая расшифровка длиннее всего остального на экране вместе
 * взятого. Грузится тоже по раскрытию — иначе каждый заход на встречу тянул бы
 * весь текст созвона ради строки «Расшифровка».
 *
 * Слушают её участники: право `meetings:read_recordings` тут ни при чём, это
 * право CRM про разговор с клиентом (DEV-2098).
 */
import { ChevronDown, ChevronRight, Volume2 } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { getMeetingAudioUrl, getMeetingTranscript, type TranscriptPhrase } from '../api'

const props = defineProps<{
  sessionId: string
  /** Говорящих модель угадала по контексту: аудио моно, разметки в нём нет */
  speakersInferred: boolean
}>()

const { t } = useI18n()

const isOpen = ref(false)
const isLoading = ref(false)
const phrases = ref<TranscriptPhrase[]>([])
const failed = ref(false)
const audioUrl = ref('')
const player = ref<HTMLAudioElement | null>(null)

const toggle = async () => {
  isOpen.value = !isOpen.value

  if (!isOpen.value || phrases.value.length || isLoading.value) return

  isLoading.value = true

  try {
    phrases.value = await getMeetingTranscript(props.sessionId)
    failed.value = false
  } catch {
    failed.value = true
  } finally {
    isLoading.value = false
  }
}

/**
 * Ссылка на аудио живёт час и берётся по кнопке: получать её вместе с текстом
 * значило бы выдавать её и тем, кто читает молча.
 */
const playAudio = async () => {
  if (audioUrl.value) return

  try {
    audioUrl.value = await getMeetingAudioUrl(props.sessionId)
  } catch {
    failed.value = true
  }
}

const speakers = computed(
  () => new Set(phrases.value.map((phrase) => phrase.speaker).filter(Boolean)).size,
)

/** Длительность — по последней реплике: другого конца у расшифровки нет */
const minutes = computed(() =>
  Math.max(1, Math.round((phrases.value[phrases.value.length - 1]?.offsetMs ?? 0) / 60_000)),
)

const stamp = (offsetMs: number): string => {
  const total = Math.floor(offsetMs / 1000)
  const seconds = total % 60

  return `${Math.floor(total / 60)}:${String(seconds).padStart(2, '0')}`
}

/** Тап по таймкоду отматывает аудио: цитату проверяют голосом, а не текстом */
const seek = (offsetMs: number) => {
  if (!player.value) return

  player.value.currentTime = offsetMs / 1000
  void player.value.play()
}
</script>

<template>
  <section class="tr">
    <button type="button" class="tr__head" @click="toggle()">
      <ChevronDown v-if="isOpen" :size="14" aria-hidden="true" />
      <ChevronRight v-else :size="14" aria-hidden="true" />

      <span class="tr__title">{{ t('meetings.transcript.title') }}</span>

      <span v-if="phrases.length" class="tr__sub">
        {{ t('meetings.transcript.summary', { minutes, speakers }) }}
      </span>
    </button>

    <template v-if="isOpen">
      <p v-if="isLoading" class="tr__note">{{ t('meetings.transcript.loading') }}</p>
      <p v-else-if="failed" class="tr__note">{{ t('meetings.transcript.failed') }}</p>
      <p v-else-if="!phrases.length" class="tr__note">{{ t('meetings.transcript.empty') }}</p>

      <template v-else>
        <div class="tr__audio">
          <button v-if="!audioUrl" type="button" class="tr__play" @click="playAudio()">
            <Volume2 :size="13" aria-hidden="true" />
            {{ t('meetings.transcript.audio') }}
          </button>

          <audio v-else ref="player" class="tr__player" controls :src="audioUrl" />
        </div>

        <ol class="tr__list">
          <li v-for="(phrase, index) in phrases" :key="index" class="tr__row">
            <button type="button" class="tr__time" @click="seek(phrase.offsetMs)">
              {{ stamp(phrase.offsetMs) }}
            </button>

            <span class="tr__speaker">{{
              phrase.speaker || t('meetings.transcript.someone')
            }}</span>
            <span class="tr__text">{{ phrase.text }}</span>
          </li>
        </ol>

        <!-- Говорящих модель угадала: аудио моно, разметки по голосам в нём
             нет. Молчать об этом нельзя — по расшифровке заводят задачи. -->
        <p v-if="speakersInferred" class="tr__note">{{ t('meetings.transcript.inferred') }}</p>
      </template>
    </template>
  </section>
</template>

<style scoped>
.tr {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid var(--color-border);
}

.tr__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
  border: none;
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}

.tr__title {
  font-weight: var(--font-weight-medium);
}

.tr__sub {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.tr__audio {
  display: flex;
}

.tr__player {
  width: 100%;
  max-width: 420px;
}

.tr__play {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-caption);
  line-height: 18px;
  cursor: pointer;
}

.tr__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* Расшифровка длиннее ленты в разы: скроллится внутри себя, иначе конспект
     и действия уезжают с экрана насовсем. */
  max-height: 320px;
  margin: 0;
  overflow-y: auto;
  padding: 0;
  list-style: none;
}

.tr__row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: var(--font-size-caption);
  line-height: 18px;
}

.tr__time {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-overline);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.tr__speaker {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.tr__text {
  color: var(--color-text-primary);
}

.tr__note {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}
</style>
